import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminMe, adminGetStats, adminSeed,
  adminGetNews, adminCreateNews, adminUpdateNews, adminDeleteNews,
  adminGetClients, adminCreateClient, adminUpdateClient, adminDeleteClient, adminUploadClientLogo, adminReorderClients,
  adminGetCountries, adminCreateCountry, adminUpdateCountry, adminDeleteCountry,
  adminGetLeads, adminDeleteLead, adminGetContacts, adminDeleteContact, adminUploadProfile, adminUploadImage, adminUploadFavicon,
  adminGetAnalytics,
  fetchSiteContent, adminUpdateSiteContent, getLogoUrl,
  adminGetFeeds, adminCreateFeed, adminToggleFeed, adminDeleteFeed, adminRefreshFeeds
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ListEditor from "../components/admin/ListEditor";
import { COUNTRY_CODES } from "../data/countryCodes";
import {
  Newspaper, Users, Globe, Download, Mail, User, Eye,
  Plus, Trash2, Pencil, Upload, LogOut, Check, X, FileText, Loader2,
  BarChart3, Settings, Palette, Briefcase, Rss, RefreshCw, ExternalLink,
  TrendingUp, Monitor, Smartphone, Tablet,
  Menu as MenuIcon, ToggleLeft, ToggleRight, ChevronUp, ChevronDown, ChevronLeft, ChevronRight
} from "lucide-react";

// Preview helper: save draft and open page
const openPreview = (draftData, pagePath = "/") => {
  localStorage.setItem("admin_preview_draft", JSON.stringify(draftData));
  window.open(`${pagePath}?preview=true`, "_blank");
};

const NAV = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "profile", label: "Profile & Bio", icon: User },
  { id: "roles", label: "Roles & Expertise", icon: Briefcase },
  { id: "cv", label: "CV Content", icon: FileText },
  { id: "branding", label: "Images & Branding", icon: Palette },
  { id: "news", label: "News & Feeds", icon: Newspaper },
  { id: "clients", label: "Clients", icon: Users },
  { id: "countries", label: "Countries", icon: Globe },
  { id: "leads", label: "Leads", icon: Download },
  { id: "contacts", label: "Messages", icon: Mail },
  { id: "settings", label: "Settings", icon: Settings },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [stats, setStats] = useState(null);
  const [siteContent, setSiteContent] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    adminMe().catch(() => navigate("/admin/login"));
    loadStats();
    loadSiteContent();
  }, [navigate]);

  const loadStats = async () => { try { setStats(await adminGetStats()); } catch {} };
  const loadSiteContent = async () => { try { setSiteContent(await fetchSiteContent()); } catch {} };

  const saveSC = async (key, value) => {
    await adminUpdateSiteContent(key, value);
    setSiteContent((prev) => ({ ...prev, [key]: value }));
  };

  const handleSeed = async () => { await adminSeed(); loadStats(); loadSiteContent(); };
  const handleLogout = () => { localStorage.removeItem("admin_token"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 bg-[#1C2B3A] transform transition-transform duration-200 lg:relative lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-white/10">
          <h1 className="font-serif text-base font-bold text-white" data-testid="admin-dashboard-title">Admin Panel</h1>
          <p className="text-xs text-white/40 font-sans mt-0.5">Portfolio CMS</p>
        </div>
        <nav className="p-3 space-y-0.5 overflow-y-auto max-h-[calc(100vh-130px)]">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setSection(n.id); setSidebarOpen(false); }} data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm font-sans transition-colors ${section === n.id ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80 hover:bg-white/5"}`}>
              <n.icon size={15} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-white/50 hover:text-white/80 text-sm font-sans" data-testid="admin-logout-btn"><LogOut size={14} /> Logout</button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E5DDD3] px-6 py-3 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1" onClick={() => setSidebarOpen(true)}><MenuIcon size={20} className="text-[#1C2B3A]" /></button>
            <h2 className="font-serif text-base font-bold text-[#1C2B3A]">{NAV.find((n) => n.id === section)?.label}</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => window.open("/", "_blank")} className="text-xs text-[#7A7A7A] hover:text-[#1C2B3A] gap-1" data-testid="view-site-btn"><ExternalLink size={12} /> View Site</Button>
            <Button variant="ghost" size="sm" onClick={handleSeed} className="text-xs text-[#7A7A7A] hover:text-[#1C2B3A]" data-testid="seed-btn">Seed Data</Button>
          </div>
        </header>
        <div className="p-6 max-w-4xl">
          {section === "overview" && <OverviewSection stats={stats} sc={siteContent} />}
          {section === "analytics" && <AnalyticsSection />}
          {section === "profile" && <ProfileSection sc={siteContent} saveSC={saveSC} />}
          {section === "roles" && <RolesSection sc={siteContent} saveSC={saveSC} />}
          {section === "cv" && <CVSection sc={siteContent} saveSC={saveSC} />}
          {section === "branding" && <BrandingSection sc={siteContent} saveSC={saveSC} />}
          {section === "news" && <NewsSection />}
          {section === "clients" && <ClientsSection />}
          {section === "countries" && <CountriesSection />}
          {section === "leads" && <LeadsSection />}
          {section === "contacts" && <ContactsSection />}
          {section === "settings" && <SettingsSection />}
        </div>
      </div>
    </div>
  );
};

// ─── Overview ──────────────────────────────

const AnalyticsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await adminGetAnalytics();
      setData(d); setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load analytics");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s for "real-time" feel
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) return <div className="flex items-center gap-2 text-[#7A7A7A]"><Loader2 className="animate-spin text-[#B8945A]" size={18} /> Loading analytics…</div>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const visitCards = [
    { label: "Today", value: data.visits.daily, unique: data.unique_visitors.daily },
    { label: "Past 7 days", value: data.visits.weekly, unique: data.unique_visitors.weekly },
    { label: "Past 30 days", value: data.visits.monthly, unique: data.unique_visitors.monthly },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl font-bold text-[#1C2B3A]">Visitor Analytics</h2>
          <p className="text-xs text-[#7A7A7A] font-sans">Refreshes every 30 seconds · last updated {new Date(data.generated_at).toLocaleTimeString()}</p>
        </div>
        <Button variant="outline" onClick={load} className="border-[#E5DDD3] text-sm gap-1.5 text-[#1C2B3A]" data-testid="analytics-refresh-btn"><RefreshCw size={14} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-visit-cards">
        {visitCards.map((c) => (
          <div key={c.label} className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid={`analytics-card-${c.label.toLowerCase().replace(/\s/g,"-")}`}>
            <p className="text-xs uppercase tracking-wider text-[#B8945A] font-sans font-bold mb-2">{c.label}</p>
            <p className="text-3xl font-serif font-bold text-[#1C2B3A]">{c.value}</p>
            <p className="text-xs text-[#7A7A7A] font-sans mt-1">Page views · {c.unique} unique visitors</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarList title="Traffic Sources" rows={data.sources} icon={Globe} testid="analytics-sources" />
        <BarList title="Top Pages" rows={data.top_pages} icon={FileText} testid="analytics-pages" />
        <BarList title="Top Countries" rows={data.countries} icon={Globe} testid="analytics-countries" />
        <BarList title="Top Cities" rows={data.cities} icon={MapPinIcon} testid="analytics-cities" />
        <BarList title="Device Breakdown" rows={data.devices} icon={Monitor} testid="analytics-devices" />
        <RecentLeads leads={data.recent_leads} totalCount={data.leads_count} />
      </div>
    </div>
  );
};

const MapPinIcon = (props) => <Globe {...props} />;

const BarList = ({ title, rows, icon: Icon, testid }) => {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid={testid}>
      <h3 className="font-serif text-sm font-bold text-[#1C2B3A] mb-4 flex items-center gap-2"><Icon size={14} className="text-[#B8945A]" /> {title}</h3>
      {rows.length === 0 ? <p className="text-xs text-[#7A7A7A] font-sans italic">No data yet</p> : (
        <div className="space-y-2.5">
          {rows.map((r, i) => {
            const pct = Math.round((r.count / total) * 100);
            return (
              <div key={`${r.label}-${i}`} className="space-y-1">
                <div className="flex justify-between items-center text-xs font-sans">
                  <span className="text-[#1C2B3A] truncate flex-1">{r.label}</span>
                  <span className="text-[#7A7A7A] flex-shrink-0 ml-3">{r.count} <span className="text-[#B8945A]">({pct}%)</span></span>
                </div>
                <div className="h-1.5 bg-[#FAF8F4] rounded-full overflow-hidden">
                  <div className="h-full bg-[#B8945A] rounded-full" style={{ width: `${pct}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RecentLeads = ({ leads, totalCount }) => (
  <div className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid="analytics-recent-leads">
    <h3 className="font-serif text-sm font-bold text-[#1C2B3A] mb-1 flex items-center gap-2"><Download size={14} className="text-[#B8945A]" /> Contact-form Leads</h3>
    <p className="text-xs text-[#7A7A7A] mb-4">{totalCount} total · showing most recent {leads.length}</p>
    {leads.length === 0 ? <p className="text-xs text-[#7A7A7A] font-sans italic">No leads yet</p> : (
      <div className="space-y-2">
        {leads.map((l) => (
          <div key={l.id} className="flex items-start justify-between gap-2 py-1.5 border-b border-[#FAF8F4] last:border-0">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-sans text-[#1C2B3A] truncate">{l.name}</p>
              <p className="text-[11px] font-sans text-[#7A7A7A] truncate">{l.email}</p>
            </div>
            <span className="text-[11px] text-[#7A7A7A] font-sans flex-shrink-0">{new Date(l.created_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── Overview ──────────────────────────────

const OverviewSection = ({ stats, sc }) => {
  if (!stats) return <Loader2 className="animate-spin text-[#B8945A]" size={24} />;
  const skillsCount = Array.isArray(sc?.skills) ? sc.skills.length : 0;
  const homepageClientsLabel = (sc?.stats && sc.stats.clients) || "—";
  const cards = [
    { label: "News Items", value: stats.news_count, icon: Newspaper, hint: `${stats.news_manual} manual · ${stats.news_feed} feed` },
    { label: "RSS Feeds", value: stats.feeds_total ?? 0, icon: Rss, hint: `${stats.feeds_active ?? 0} active` },
    { label: "Client Logos", value: stats.clients_count, icon: Users, hint: `Homepage shows: ${homepageClientsLabel}` },
    { label: "Countries on Map", value: stats.countries_count, icon: Globe },
    { label: "Skills", value: skillsCount, icon: Briefcase },
    { label: "Leads", value: stats.leads_count, icon: Download },
    { label: "Messages", value: stats.contacts_count, icon: Mail },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-lg border border-[#E5DDD3] p-4" data-testid={`stat-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
          <c.icon size={18} className="text-[#B8945A] mb-2" />
          <p className="text-2xl font-serif font-bold text-[#1C2B3A]">{c.value}</p>
          <p className="text-xs text-[#7A7A7A] font-sans">{c.label}</p>
          {c.hint && <p className="text-[10px] text-[#B8945A]/80 font-sans mt-1">{c.hint}</p>}
        </div>
      ))}
    </div>
  );
};

// ─── Profile ───────────────────────────────

const ProfileSection = ({ sc, saveSC }) => {
  const pi = sc.personal_info || {};
  const ci = sc.contact_info || {};
  const so = sc.social || {};
  const qt = sc.quote || "";
  const ht = sc.hero_tagline || "";

  const [profile, setProfile] = useState(pi);
  const [contact, setContact] = useState(ci);
  const [social, setSocial] = useState(so);
  const [quote, setQuote] = useState(qt);
  const [heroTagline, setHeroTagline] = useState(ht);
  const [saved, setSaved] = useState("");

  useEffect(() => { setProfile(sc.personal_info || {}); setContact(sc.contact_info || {}); setSocial(sc.social || {}); setQuote(sc.quote || ""); setHeroTagline(sc.hero_tagline || ""); }, [sc]);

  const save = async (key, val) => { await saveSC(key, val); setSaved(key); setTimeout(() => setSaved(""), 2000); };

  const handlePreview = () => {
    openPreview({ personal_info: profile, contact_info: contact, social, quote, hero_tagline: heroTagline }, "/");
  };

  return (
    <div className="space-y-6" data-testid="profile-editor">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handlePreview} className="border-[#E5DDD3] text-sm gap-1.5 text-[#1C2B3A]" data-testid="preview-profile-btn"><Eye size={14} /> Preview on Site</Button>
      </div>
      <SectionCard title="Hero Tagline (shown above your name on the homepage)" saved={saved === "hero_tagline"} onSave={() => save("hero_tagline", heroTagline)}>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs text-[#7A7A7A]">Tagline</Label>
          <Input data-testid="hero-tagline-input" value={heroTagline} onChange={(e) => setHeroTagline(e.target.value)} placeholder="Strategy · Trade · Development" className="border-[#E5DDD3] text-sm" />
        </div>
      </SectionCard>

      <SectionCard title="Personal Information" saved={saved === "personal_info"} onSave={() => save("personal_info", profile)}>
        <Field label="Full Name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} />
        <Field label="Title" value={profile.title} onChange={(v) => setProfile({ ...profile, title: v })} />
        <Field label="Subtitle" value={profile.subtitle} onChange={(v) => setProfile({ ...profile, subtitle: v })} wide />
        <Field label="Tagline" value={profile.tagline} onChange={(v) => setProfile({ ...profile, tagline: v })} wide />
        <Field label="Short Bio" type="textarea" value={profile.shortBio} onChange={(v) => setProfile({ ...profile, shortBio: v })} wide />
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs text-[#7A7A7A]">Full Bio (one paragraph per line)</Label>
          <Textarea value={(profile.fullBio || []).join("\n\n")} onChange={(e) => setProfile({ ...profile, fullBio: e.target.value.split("\n\n").filter(Boolean) })} className="border-[#E5DDD3] text-sm min-h-[120px]" />
        </div>
      </SectionCard>

      <SectionCard title="Contact Details" saved={saved === "contact_info"} onSave={() => save("contact_info", contact)}>
        <Field label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
        <Field label="Work Email" value={contact.emailWork} onChange={(v) => setContact({ ...contact, emailWork: v })} />
        <Field label="Academic Email" value={contact.emailAcademic} onChange={(v) => setContact({ ...contact, emailAcademic: v })} />
        <Field label="Phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
        <Field label="Phone (UK)" value={contact.phoneUK} onChange={(v) => setContact({ ...contact, phoneUK: v })} />
        <Field label="Address" value={contact.address} onChange={(v) => setContact({ ...contact, address: v })} />
        <Field label="Website" value={contact.website} onChange={(v) => setContact({ ...contact, website: v })} />
      </SectionCard>

      <SectionCard title="Social Links" saved={saved === "social"} onSave={() => save("social", social)}>
        <Field label="LinkedIn URL" value={social.linkedin} onChange={(v) => setSocial({ ...social, linkedin: v })} wide />
      </SectionCard>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-2">Additional Social Links</h3>
        <p className="text-xs text-[#7A7A7A] mb-4">Add Substack, Twitter/X, YouTube, GitHub, Medium, Instagram, Facebook, Threads, Mastodon, or any custom link. Each link shows as an icon in the Contact page and Footer.</p>
        <ListEditor title="Social Link" items={sc.social_links || []} fields={[
          { key: "label", label: "Label", placeholder: "Substack" },
          { key: "icon", label: "Icon Name", placeholder: "globe (see icon list below)" },
          { key: "url", label: "URL", placeholder: "https://abhilash.substack.com", wide: true },
        ]} onSave={(v) => saveSC("social_links", v)} />
        <p className="text-[11px] text-[#7A7A7A] mt-3"><strong>Available icons:</strong> linkedin, twitter, youtube, github, instagram, facebook, mail, globe, link, rss, substack, bluesky, podcast, music, video, image, briefcase, externallink</p>
      </div>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid="quotes-editor">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-2">Rotating Quotes (About page)</h3>
        <p className="text-xs text-[#7A7A7A] mb-4">Add as many quotes as you like — they will rotate every 5 seconds on the About page. One quote shows static; multiple rotate.</p>
        <ListEditor title="Quote" items={sc.quotes || (sc.quote ? [{ text: sc.quote }] : [])} fields={[
          { key: "text", label: "Quote text", type: "textarea", wide: true, placeholder: "Strategy is policy meeting execution..." },
          { key: "author", label: "Author (optional)", required: false, placeholder: "Leave blank for unattributed" },
        ]} onSave={(v) => { saveSC("quotes", v); if (sc.quote) saveSC("quote", ""); }} />
      </div>
    </div>
  );
};

// ─── Roles & Expertise ─────────────────────

const RolesSection = ({ sc, saveSC }) => {
  const handlePreview = () => {
    openPreview({ current_roles: sc.current_roles, expertise: sc.expertise, focus_sectors: sc.focus_sectors, skills: sc.skills }, "/about");
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handlePreview} className="border-[#E5DDD3] text-sm gap-1.5 text-[#1C2B3A]" data-testid="preview-roles-btn"><Eye size={14} /> Preview on Site</Button>
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Current Roles</h3>
        <ListEditor title="Role" items={sc.current_roles || []} fields={[
          { key: "title", label: "Title", placeholder: "Partner & Country Director" },
          { key: "organization", label: "Organization", placeholder: "Expectation State" },
          { key: "url", label: "Organization URL (link)", required: false, placeholder: "https://expectationstate.com", wide: true },
          { key: "location", label: "Location", placeholder: "London, UK", required: false },
          { key: "period", label: "Period", placeholder: "2024 - Present", required: false },
          { key: "logo_url", label: "Organization Logo", type: "image", required: false, wide: true },
          { key: "description", label: "Description", type: "textarea", wide: true },
        ]} onSave={(v) => saveSC("current_roles", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Expertise</h3>
        <ListEditor title="Expertise" items={sc.expertise || []} fields={[
          { key: "name", label: "Name", placeholder: "Trade Facilitation" },
          { key: "icon", label: "Icon", placeholder: "Globe, FileText, Briefcase...", required: false },
        ]} onSave={(v) => saveSC("expertise", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Focus Sectors</h3>
        <FocusSectorsEditor sectors={sc.focus_sectors || []} onSave={(v) => saveSC("focus_sectors", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Skills</h3>
        <p className="font-sans text-xs text-[#7A7A7A] mb-3">Add individual skills shown as pills on the About page.</p>
        <SkillsEditor skills={sc.skills || []} onSave={(v) => saveSC("skills", v)} />
      </div>
    </div>
  );
};

const FocusSectorsEditor = ({ sectors, onSave }) => {
  const [newSector, setNewSector] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input value={newSector} onChange={(e) => setNewSector(e.target.value)} placeholder="New sector name" className="border-[#E5DDD3] text-sm flex-1" />
        <Button onClick={() => { if (newSector.trim()) { onSave([...sectors, newSector.trim()]); setNewSector(""); } }} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1"><Plus size={14} /> Add</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {sectors.map((s, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5DDD3] rounded-full text-sm font-sans text-[#4A4A4A]">
            {s}<button onClick={() => onSave(sectors.filter((_, j) => j !== i))} className="text-[#7A7A7A] hover:text-red-500"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const SkillsEditor = ({ skills, onSave }) => {
  const [newSkill, setNewSkill] = useState("");
  const add = () => { if (newSkill.trim() && !skills.includes(newSkill.trim())) { onSave([...skills, newSkill.trim()]); setNewSkill(""); } };
  return (
    <div className="space-y-3" data-testid="skills-editor">
      <div className="flex gap-2">
        <Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="e.g. Strategic Planning" className="border-[#E5DDD3] text-sm flex-1" data-testid="skill-input" />
        <Button onClick={add} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1" data-testid="skill-add-btn"><Plus size={14} /> Add Skill</Button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="skills-list">
        {skills.map((s, i) => (
          <span key={`${s}-${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#B8945A]/40 rounded-full text-sm font-sans text-[#1C2B3A]" data-testid={`skill-chip-${i}`}>
            {s}<button onClick={() => onSave(skills.filter((_, j) => j !== i))} className="text-[#7A7A7A] hover:text-red-500" data-testid={`skill-remove-${i}`}><X size={12} /></button>
          </span>
        ))}
        {skills.length === 0 && <p className="text-xs text-[#7A7A7A] font-sans italic">No skills added yet.</p>}
      </div>
    </div>
  );
};

// ─── CV Content ────────────────────────────

const CVSection = ({ sc, saveSC }) => {
  const handlePreview = () => {
    openPreview({ experience: sc.experience, education: sc.education, publications: sc.publications, certifications: sc.certifications, memberships: sc.memberships, awards: sc.awards }, "/cv");
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handlePreview} className="border-[#E5DDD3] text-sm gap-1.5 text-[#1C2B3A]" data-testid="preview-cv-btn"><Eye size={14} /> Preview on Site</Button>
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Work Experience</h3>
        <ListEditor title="Experience" items={sc.experience || []} fields={[
          { key: "title", label: "Title" }, { key: "organization", label: "Organization" },
          { key: "url", label: "Organization URL (link)", required: false, placeholder: "https://...", wide: true },
          { key: "location", label: "Location" }, { key: "period", label: "Period" },
          { key: "description", label: "Description", type: "textarea", wide: true },
          { key: "highlights", label: "Key Highlights (one per line)", type: "array", wide: true, required: false },
        ]} onSave={(v) => saveSC("experience", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Education</h3>
        <ListEditor title="Education" items={sc.education || []} fields={[
          { key: "degree", label: "Degree" }, { key: "institution", label: "Institution" },
          { key: "url", label: "Institution URL (link)", required: false, placeholder: "https://...", wide: true },
          { key: "location", label: "Location" }, { key: "period", label: "Period" },
          { key: "logo_url", label: "Institution Logo", type: "image", required: false, wide: true },
          { key: "note", label: "Note", required: false, wide: true },
        ]} onSave={(v) => saveSC("education", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Publications</h3>
        <ListEditor title="Publication" items={sc.publications || []} fields={[
          { key: "title", label: "Title", wide: true }, { key: "publication", label: "Publication" },
          { key: "date", label: "Date" }, { key: "coAuthors", label: "Co-Authors", required: false },
          { key: "link", label: "Link (URL) — title becomes clickable on the CV page", required: false, wide: true, placeholder: "https://..." },
        ]} onSave={(v) => saveSC("publications", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Certifications</h3>
        <ListEditor title="Certification" items={sc.certifications || []} fields={[
          { key: "title", label: "Title" }, { key: "institution", label: "Institution" },
        ]} onSave={(v) => saveSC("certifications", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Memberships</h3>
        <ListEditor title="Membership" items={sc.memberships || []} fields={[
          { key: "title", label: "Title" }, { key: "organization", label: "Organization" },
          { key: "url", label: "Organization URL (link)", required: false, placeholder: "https://...", wide: true },
          { key: "logo_url", label: "Organization Logo", type: "image", required: false, wide: true },
        ]} onSave={(v) => saveSC("memberships", v)} />
      </div>
      <div>
        <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-4">Awards</h3>
        <ListEditor title="Award" items={sc.awards || []} fields={[
          { key: "title", label: "Title" }, { key: "institution", label: "Institution" },
          { key: "year", label: "Year" },
        ]} onSave={(v) => saveSC("awards", v)} />
      </div>
    </div>
  );
};

// ─── Branding ──────────────────────────────

const BrandingSection = ({ sc, saveSC }) => {
  const stats = sc.stats || {};
  const images = sc.images || {};
  const heroImages = Array.isArray(sc.hero_images) ? sc.hero_images : (images.consulting ? [images.consulting] : []);
  const aboutImages = Array.isArray(sc.about_images) ? sc.about_images : (images.diplomatic ? [images.diplomatic] : []);
  const [localStats, setLocalStats] = useState(stats);
  const [localImages, setLocalImages] = useState(images);
  const [localHero, setLocalHero] = useState(heroImages);
  const [localAbout, setLocalAbout] = useState(aboutImages);
  const [saved, setSaved] = useState("");
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    setLocalStats(sc.stats || {});
    setLocalImages(sc.images || {});
    setLocalHero(Array.isArray(sc.hero_images) ? sc.hero_images : ((sc.images || {}).consulting ? [(sc.images || {}).consulting] : []));
    setLocalAbout(Array.isArray(sc.about_images) ? sc.about_images : ((sc.images || {}).diplomatic ? [(sc.images || {}).diplomatic] : []));
  }, [sc]);

  const saveField = async (key, val) => { await saveSC(key, val); setSaved(key); setTimeout(() => setSaved(""), 2000); };

  const handleImageUpload = async (imageKey, file) => {
    setUploading(imageKey);
    try {
      const res = await adminUploadImage(file);
      const url = res.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${res.url}` : res.url;
      const newImages = { ...localImages, [imageKey]: url };
      setLocalImages(newImages);
      await saveSC("images", newImages);
    } catch {}
    setUploading("");
  };

  const handleFavicon = async (file) => {
    setUploading("favicon");
    try { await adminUploadFavicon(file); setSaved("favicon"); setTimeout(() => setSaved(""), 2000); } catch {}
    setUploading("");
  };

  const handlePreview = () => {
    openPreview({ stats: localStats, images: localImages, hero_images: localHero, about_images: localAbout }, "/");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handlePreview} className="border-[#E5DDD3] text-sm gap-1.5 text-[#1C2B3A]" data-testid="preview-branding-btn"><Eye size={14} /> Preview on Site</Button>
      </div>
      <SectionCard title="Stats (displayed on homepage)" saved={saved === "stats"} onSave={() => saveField("stats", localStats)}>
        <Field label="Countries" value={localStats.countries} onChange={(v) => setLocalStats({ ...localStats, countries: v })} />
        <Field label="Continents" value={localStats.continents} onChange={(v) => setLocalStats({ ...localStats, continents: v })} />
        <Field label="Years Experience" value={localStats.yearsExperience} onChange={(v) => setLocalStats({ ...localStats, yearsExperience: v })} />
        <Field label="Clients" value={localStats.clients} onChange={(v) => setLocalStats({ ...localStats, clients: v })} />
      </SectionCard>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid="hero-images-editor">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-1">Homepage / About Preview Carousel</h3>
        <p className="font-sans text-xs text-[#7A7A7A] mb-4">Add multiple images. They auto-rotate every 3 seconds on the homepage.</p>
        <MultiImageEditor images={localHero} onChange={setLocalHero} testid="hero" />
        <Button onClick={() => saveField("hero_images", localHero)} className="mt-4 bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1" data-testid="save-hero-images-btn">
          {saved === "hero_images" ? <><Check size={14} /> Saved</> : "Save Hero Images"}
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5" data-testid="about-images-editor">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-1">About Page Profile Carousel</h3>
        <p className="font-sans text-xs text-[#7A7A7A] mb-4">Add multiple profile images. They auto-rotate every 3 seconds on the About page.</p>
        <MultiImageEditor images={localAbout} onChange={setLocalAbout} testid="about" />
        <Button onClick={() => saveField("about_images", localAbout)} className="mt-4 bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1" data-testid="save-about-images-btn">
          {saved === "about_images" ? <><Check size={14} /> Saved</> : "Save About Images"}
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-4">Other Section Images</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[{ key: "university", label: "Education Section" }, { key: "cityscape", label: "Contact / CTA Background" }].map((img) => (
            <div key={img.key} className="space-y-2">
              <Label className="text-xs text-[#7A7A7A]">{img.label}</Label>
              <div className="flex gap-2 items-center">
                <Input value={localImages[img.key] || ""} onChange={(e) => setLocalImages({ ...localImages, [img.key]: e.target.value })} placeholder="Image URL" className="border-[#E5DDD3] text-xs flex-1" />
                <label className="cursor-pointer flex-shrink-0">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(img.key, e.target.files[0])} />
                  <div className="h-9 px-3 flex items-center gap-1 rounded-md border border-[#E5DDD3] hover:border-[#B8945A] text-xs font-sans text-[#7A7A7A]"><Upload size={12} /> {uploading === img.key ? "..." : "Upload"}</div>
                </label>
              </div>
              {localImages[img.key] && <img src={localImages[img.key]} alt={img.label} className="w-full h-24 object-cover rounded border border-[#E5DDD3]" />}
            </div>
          ))}
        </div>
        <Button onClick={() => saveField("images", localImages)} className="mt-4 bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1">
          {saved === "images" ? <><Check size={14} /> Saved</> : "Save Images"}
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-2">Favicon</h3>
        <p className="font-sans text-xs text-[#7A7A7A] mb-4">Upload a .ico or .png file (32×32 recommended) to use as the browser-tab icon. Reload the site after uploading; some browsers cache favicons aggressively (try a hard reload: Ctrl+Shift+R).</p>
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5DDD3] hover:border-[#B8945A] text-sm font-sans text-[#1C2B3A]">
          <input type="file" accept=".ico,.png,.svg" className="hidden" onChange={(e) => e.target.files[0] && handleFavicon(e.target.files[0])} data-testid="favicon-upload" />
          <Upload size={14} /> {uploading === "favicon" ? "Uploading..." : "Choose Favicon"}
        </label>
        {saved === "favicon" && <span className="ml-3 text-green-600 text-sm font-sans"><Check size={14} className="inline" /> Updated — reload the site to see it</span>}
        {sc.favicon?.url && (
          <div className="mt-3 flex items-center gap-2"><span className="text-xs text-[#7A7A7A] font-sans">Current:</span><img src={sc.favicon.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${sc.favicon.url}` : sc.favicon.url} alt="favicon" className="w-8 h-8 border border-[#E5DDD3] rounded" /></div>
        )}
      </div>
    </div>
  );
};

const MultiImageEditor = ({ images, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      const url = res.url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${res.url}` : res.url;
      onChange([...images, url]);
    } catch {}
    setUploading(false);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const removeAt = (i) => onChange(images.filter((_, j) => j !== i));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`${testid}-images-list`}>
        {images.map((url, i) => (
          <div key={`${url}-${i}`} className="relative group border border-[#E5DDD3] rounded-lg overflow-hidden bg-[#FAF8F4]" data-testid={`${testid}-image-${i}`}>
            <img src={url} alt={`Slide ${i + 1}`} className="w-full h-28 object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between px-2 py-1.5">
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-white disabled:opacity-30"><ChevronLeft size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === images.length - 1} className="text-white disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
              <span className="text-white text-[10px] font-sans">{i + 1}/{images.length}</span>
              <button onClick={() => removeAt(i)} className="text-white hover:text-red-300" data-testid={`${testid}-image-remove-${i}`}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        <label className="cursor-pointer flex flex-col items-center justify-center gap-1 h-28 rounded-lg border-2 border-dashed border-[#E5DDD3] hover:border-[#B8945A] text-[#7A7A7A] hover:text-[#B8945A] transition-colors" data-testid={`${testid}-image-add`}>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])} />
          <Upload size={16} /><span className="text-[11px] font-sans">{uploading ? "Uploading..." : "Add image"}</span>
        </label>
      </div>
      <div className="flex gap-2 items-center">
        <Input placeholder="Or paste an image URL and press Enter" className="border-[#E5DDD3] text-sm flex-1" onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.value.trim()) {
            e.preventDefault();
            onChange([...images, e.target.value.trim()]);
            e.target.value = "";
          }
        }} />
      </div>
    </div>
  );
};

// ─── News & Feeds ──────────────────────────

const NewsSection = () => {
  const [items, setItems] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [form, setForm] = useState({ headline: "", category: "", link: "" });
  const [feedForm, setFeedForm] = useState({ name: "", url: "", category: "" });
  const [editId, setEditId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [showFeeds, setShowFeeds] = useState(false);

  const loadNews = useCallback(async () => { setItems(await adminGetNews()); }, []);
  const loadFeeds = useCallback(async () => { setFeeds(await adminGetFeeds()); }, []);
  useEffect(() => { loadNews(); loadFeeds(); }, [loadNews, loadFeeds]);

  const handleSaveNews = async () => {
    if (!form.headline || !form.category) return;
    if (editId) await adminUpdateNews(editId, form); else await adminCreateNews(form);
    setForm({ headline: "", category: "", link: "" }); setEditId(null); loadNews();
  };

  const handleAddFeed = async () => {
    if (!feedForm.name || !feedForm.url) return;
    await adminCreateFeed({ ...feedForm, category: feedForm.category || "Feed" });
    setFeedForm({ name: "", url: "", category: "" });
    loadFeeds();
  };

  const handleRefresh = async () => {
    setRefreshing(true); setRefreshResult(null);
    try {
      const res = await adminRefreshFeeds();
      setRefreshResult(res);
      loadNews();
    } catch { setRefreshResult({ error: true }); }
    setRefreshing(false);
  };

  const manualItems = items.filter((i) => !i.source || i.source !== "feed");
  const feedItems = items.filter((i) => i.source === "feed");

  return (
    <div className="space-y-6" data-testid="news-manager">
      {/* Manual News */}
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-sm font-bold text-[#1C2B3A] mb-3">{editId ? "Edit" : "Add"} Manual Headline</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2 space-y-1"><Label className="text-xs text-[#7A7A7A]">Headline</Label><Input data-testid="news-headline-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Category</Label><Input data-testid="news-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Link (optional)</Label><Input data-testid="news-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className="border-[#E5DDD3] text-sm" /></div>
          <div className="flex gap-2">
            <Button data-testid="news-save-btn" onClick={handleSaveNews} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1 flex-1">{editId ? <Check size={14} /> : <Plus size={14} />} {editId ? "Update" : "Add"}</Button>
            {editId && <Button variant="outline" size="icon" onClick={() => { setEditId(null); setForm({ headline: "", category: "", link: "" }); }}><X size={14} /></Button>}
          </div>
        </div>
      </div>

      {/* Feed Management */}
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Rss size={16} className="text-[#B8945A]" />
            <h3 className="font-serif text-sm font-bold text-[#1C2B3A]">RSS Feed Sources</h3>
            <span className="text-xs text-[#7A7A7A] font-sans">({feeds.length} feeds)</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFeeds(!showFeeds)} className="text-xs border-[#E5DDD3] gap-1" data-testid="toggle-feeds-btn">
              {showFeeds ? "Hide" : "Manage"} Feeds
            </Button>
            <Button size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-[#B8945A] hover:bg-[#96783E] text-white text-xs gap-1" data-testid="refresh-feeds-btn">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Fetching..." : "Refresh Feeds"}
            </Button>
          </div>
        </div>

        {refreshResult && !refreshResult.error && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm font-sans text-green-700" data-testid="refresh-result">
            Imported {refreshResult.imported} new headlines
            {refreshResult.errors?.length > 0 && <span className="text-amber-600 ml-2">({refreshResult.errors.length} feeds had errors)</span>}
          </div>
        )}

        {showFeeds && (
          <div className="space-y-4">
            {/* Add feed */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 bg-[#FAF8F4] rounded-lg">
              <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Feed Name</Label><Input value={feedForm.name} onChange={(e) => setFeedForm({ ...feedForm, name: e.target.value })} placeholder="World Bank Blog" className="border-[#E5DDD3] text-sm" data-testid="feed-name-input" /></div>
              <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">RSS URL</Label><Input value={feedForm.url} onChange={(e) => setFeedForm({ ...feedForm, url: e.target.value })} placeholder="https://..." className="border-[#E5DDD3] text-sm" data-testid="feed-url-input" /></div>
              <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Category</Label><Input value={feedForm.category} onChange={(e) => setFeedForm({ ...feedForm, category: e.target.value })} placeholder="Trade, Policy..." className="border-[#E5DDD3] text-sm" data-testid="feed-category-input" /></div>
              <Button onClick={handleAddFeed} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1" data-testid="feed-add-btn"><Plus size={14} /> Add Feed</Button>
            </div>
            {/* Feed list */}
            <div className="space-y-2">
              {feeds.map((feed) => (
                <div key={feed.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#E5DDD3] bg-white">
                  <button onClick={async () => { await adminToggleFeed(feed.id); loadFeeds(); }} className="flex-shrink-0" data-testid={`feed-toggle-${feed.id}`}>
                    {feed.enabled ? <ToggleRight size={22} className="text-green-500" /> : <ToggleLeft size={22} className="text-[#D5CFC7]" />}
                  </button>
                  <Rss size={14} className={`flex-shrink-0 ${feed.enabled ? "text-[#B8945A]" : "text-[#D5CFC7]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`font-sans text-sm truncate ${feed.enabled ? "text-[#1C2B3A]" : "text-[#7A7A7A]"}`}>{feed.name}</p>
                    <p className="font-sans text-xs text-[#7A7A7A] truncate">{feed.url}</p>
                  </div>
                  <span className="text-xs text-[#B8945A] font-sans flex-shrink-0 px-2 py-0.5 bg-[#B8945A]/10 rounded-full">{feed.category}</span>
                  {feed.last_fetched && <span className="text-[10px] text-[#7A7A7A] font-sans flex-shrink-0">Last: {new Date(feed.last_fetched).toLocaleDateString()}</span>}
                  <Button variant="ghost" size="icon" onClick={async () => { await adminDeleteFeed(feed.id); loadFeeds(); loadNews(); }} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600 flex-shrink-0"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Manual headlines */}
      {manualItems.length > 0 && (
        <div>
          <h4 className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-2">Manual Headlines ({manualItems.length})</h4>
          <div className="space-y-2">
            {manualItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg border border-[#E5DDD3] p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0"><p className="font-sans text-sm text-[#1C2B3A] truncate">{item.headline}</p><p className="font-sans text-xs text-[#B8945A]">{item.category}{item.link && <span className="ml-2 text-[#7A7A7A]">· <a href={item.link} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#B8945A]">link</a></span>}</p></div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => { setEditId(item.id); setForm({ headline: item.headline, category: item.category, link: item.link || "" }); }} className="h-8 w-8 text-[#7A7A7A] hover:text-[#1C2B3A]"><Pencil size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={async () => { await adminDeleteNews(item.id); loadNews(); }} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600"><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feed-imported headlines */}
      {feedItems.length > 0 && (
        <div>
          <h4 className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-2">Feed-Imported Headlines ({feedItems.length})</h4>
          <div className="space-y-2">
            {feedItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg border border-[#E5DDD3] p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-[#1C2B3A] truncate">{item.headline}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-sans text-xs text-[#B8945A]">{item.category}</span>
                    <span className="text-[10px] text-[#7A7A7A] px-1.5 py-0.5 bg-[#FAF8F4] rounded">via {item.source_feed_name}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-[#FAF8F4] text-[#7A7A7A] hover:text-[#B8945A]"><ExternalLink size={14} /></a>}
                  <Button variant="ghost" size="icon" onClick={async () => { await adminDeleteNews(item.id); loadNews(); }} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600"><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Clients ───────────────────────────────

const ClientsSection = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", short: "", url: "" });
  const [editId, setEditId] = useState(null);
  const load = useCallback(async () => { setItems(await adminGetClients()); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name || !form.short) return;
    if (editId) await adminUpdateClient(editId, form); else await adminCreateClient(form);
    setForm({ name: "", short: "", url: "" }); setEditId(null); load();
  };

  const moveClient = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    try { await adminReorderClients(next.map((c) => c.id)); } catch { load(); }
  };

  return (
    <div className="space-y-4" data-testid="clients-manager">
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-sm font-bold text-[#1C2B3A] mb-3">{editId ? "Edit" : "Add"} Client</h3>
        <p className="text-[11px] text-[#7A7A7A] mb-3">Use ↑ / ↓ buttons on each client to reorder the homepage marquee. Adding a URL makes the client logo clickable on the homepage.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Full Name</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Short Name</Label><Input data-testid="client-short-input" value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="space-y-1 md:col-span-2"><Label className="text-xs text-[#7A7A7A]">URL (optional — makes logo clickable)</Label><Input data-testid="client-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://worldbank.org" className="border-[#E5DDD3] text-sm" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button data-testid="client-save-btn" onClick={handleSave} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1">{editId ? <Check size={14} /> : <Plus size={14} />} {editId ? "Update" : "Add"}</Button>
          {editId && <Button variant="outline" size="icon" onClick={() => { setEditId(null); setForm({ name: "", short: "", url: "" }); }}><X size={14} /></Button>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((item, i) => (
          <div key={item.id} className="bg-white rounded-lg border border-[#E5DDD3] p-4 flex items-center gap-3" data-testid={`client-row-${item.id}`}>
            <div className="flex flex-col flex-shrink-0">
              <button onClick={() => moveClient(i, -1)} disabled={i === 0} className="text-[#D5CFC7] hover:text-[#B8945A] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`client-move-up-${i}`} aria-label="Move up"><ChevronUp size={14} /></button>
              <button onClick={() => moveClient(i, 1)} disabled={i === items.length - 1} className="text-[#D5CFC7] hover:text-[#B8945A] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`client-move-down-${i}`} aria-label="Move down"><ChevronDown size={14} /></button>
            </div>
            <label className="cursor-pointer group/logo flex-shrink-0" title="Click to upload/replace logo">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => { if (e.target.files[0]) { await adminUploadClientLogo(item.id, e.target.files[0]); load(); } }} data-testid={`client-logo-upload-${item.id}`} />
              <div className="relative w-12 h-12 rounded border-2 border-dashed border-[#E5DDD3] hover:border-[#B8945A] flex items-center justify-center bg-[#FAF8F4] overflow-hidden transition-colors">
                {item.logo_path ? (
                  <>
                    <img src={getLogoUrl(item.logo_path)} alt={item.short} className="w-full h-full object-contain" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/logo:opacity-100 transition-opacity flex items-center justify-center"><Upload size={14} className="text-white" /></div>
                  </>
                ) : (
                  <Upload size={14} className="text-[#B8945A]" />
                )}
              </div>
            </label>
            <div className="flex-1 min-w-0"><p className="font-sans text-sm text-[#1C2B3A] truncate">{item.name}</p><p className="font-sans text-xs text-[#7A7A7A]">{item.short}{!item.logo_path && <span className="ml-2 text-[#B8945A]">· Click logo box to upload</span>}</p></div>
            <div className="flex gap-1 flex-shrink-0">
              <Button variant="ghost" size="icon" onClick={() => { setEditId(item.id); setForm({ name: item.name, short: item.short, url: item.url || "" }); }} className="h-8 w-8 text-[#7A7A7A] hover:text-[#1C2B3A]" title="Edit name"><Pencil size={14} /></Button>
              <Button variant="ghost" size="icon" onClick={async () => { await adminDeleteClient(item.id); load(); }} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Countries ─────────────────────────────

const CountriesSection = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ country_code: "", name: "", project_details: "" });
  const [editId, setEditId] = useState(null);
  const load = useCallback(async () => { setItems(await adminGetCountries()); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.country_code || !form.name || !form.project_details) return;
    if (editId) await adminUpdateCountry(editId, form); else await adminCreateCountry(form);
    setForm({ country_code: "", name: "", project_details: "" }); setEditId(null); load();
  };

  const pickCountry = (code) => {
    const found = COUNTRY_CODES.find((c) => c.code === code);
    if (found) setForm({ ...form, country_code: found.code, name: found.name });
  };

  return (
    <div className="space-y-4" data-testid="countries-manager">
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-sm font-bold text-[#1C2B3A] mb-1">{editId ? "Edit" : "Add"} Country</h3>
        <p className="text-[11px] text-[#7A7A7A] mb-3">Pick a country from the dropdown to auto-fill its ISO numeric code (the map uses ISO 3166-1 numeric codes — e.g. United States = <strong>840</strong>, India = <strong>356</strong>, United Kingdom = <strong>826</strong>).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1 md:col-span-1">
            <Label className="text-xs text-[#7A7A7A]">Pick from list (recommended)</Label>
            <select data-testid="country-picker" value={form.country_code} onChange={(e) => pickCountry(e.target.value)} className="w-full h-9 px-3 rounded-md border border-[#E5DDD3] text-sm bg-white text-[#1C2B3A]">
              <option value="">— Select a country —</option>
              {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Country Code</Label><Input data-testid="country-code-input" value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs text-[#7A7A7A]">Display Name</Label><Input data-testid="country-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E5DDD3] text-sm" /></div>
          <div className="md:col-span-3 space-y-1"><Label className="text-xs text-[#7A7A7A]">Project Details</Label><Textarea data-testid="country-details-input" value={form.project_details} onChange={(e) => setForm({ ...form, project_details: e.target.value })} className="border-[#E5DDD3] text-sm min-h-[60px]" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button data-testid="country-save-btn" onClick={handleSave} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1">{editId ? <Check size={14} /> : <Plus size={14} />} {editId ? "Update" : "Add"}</Button>
          {editId && <Button variant="outline" onClick={() => { setEditId(null); setForm({ country_code: "", name: "", project_details: "" }); }} className="text-sm"><X size={14} /> Cancel</Button>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border border-[#E5DDD3] p-4">
            <div className="flex items-center justify-between mb-2">
              <div><span className="font-sans text-sm font-medium text-[#1C2B3A]">{item.name}</span><span className="text-xs text-[#7A7A7A] ml-2">({item.country_code})</span></div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditId(item.id); setForm({ country_code: item.country_code, name: item.name, project_details: item.project_details }); }} className="h-8 w-8 text-[#7A7A7A] hover:text-[#1C2B3A]"><Pencil size={14} /></Button>
                <Button variant="ghost" size="icon" onClick={async () => { await adminDeleteCountry(item.id); load(); }} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600"><Trash2 size={14} /></Button>
              </div>
            </div>
            <p className="font-sans text-xs text-[#4A4A4A] line-clamp-2">{item.project_details}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Leads ─────────────────────────────────

const LeadsSection = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { adminGetLeads().then(setItems).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await adminDeleteLead(id); load();
  };
  return (
    <div className="bg-white rounded-lg border border-[#E5DDD3]" data-testid="leads-viewer">
      <div className="p-4 border-b border-[#E5DDD3]"><h3 className="font-serif text-base font-bold text-[#1C2B3A]">Profile Download Leads ({items.length})</h3></div>
      {items.length === 0 ? <p className="p-6 text-center text-sm text-[#7A7A7A]">No leads yet</p> : (
        <div className="divide-y divide-[#E5DDD3]">{items.map((item) => (
          <div key={item.id} className="p-4 flex items-center gap-4" data-testid={`lead-row-${item.id}`}>
            <div className="w-9 h-9 rounded-full bg-[#1C2B3A] flex items-center justify-center flex-shrink-0"><span className="text-white text-xs font-bold">{item.name.charAt(0).toUpperCase()}</span></div>
            <div className="flex-1 min-w-0"><p className="font-sans text-sm text-[#1C2B3A] font-medium">{item.name}</p><p className="font-sans text-xs text-[#7A7A7A]">{item.email}</p></div>
            <p className="font-sans text-xs text-[#7A7A7A] flex-shrink-0">{new Date(item.created_at).toLocaleDateString()}</p>
            <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600 flex-shrink-0" data-testid={`lead-delete-${item.id}`}><Trash2 size={14} /></Button>
          </div>
        ))}</div>
      )}
    </div>
  );
};

// ─── Contacts ──────────────────────────────

const ContactsSection = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { adminGetContacts().then(setItems).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    await adminDeleteContact(id); load();
  };
  return (
    <div className="bg-white rounded-lg border border-[#E5DDD3]" data-testid="contacts-viewer">
      <div className="p-4 border-b border-[#E5DDD3]"><h3 className="font-serif text-base font-bold text-[#1C2B3A]">Contact Messages ({items.length})</h3></div>
      {items.length === 0 ? <p className="p-6 text-center text-sm text-[#7A7A7A]">No messages yet</p> : (
        <div className="divide-y divide-[#E5DDD3]">{items.map((item) => (
          <div key={item.id} className="p-4" data-testid={`contact-row-${item.id}`}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex-1 min-w-0"><span className="font-sans text-sm font-medium text-[#1C2B3A]">{item.name}</span><span className="text-xs text-[#7A7A7A] ml-2">{item.email}</span></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-[#7A7A7A]">{new Date(item.created_at).toLocaleDateString()}</span>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600" data-testid={`contact-delete-${item.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
            {item.subject && <p className="text-xs text-[#B8945A] mb-1">{item.subject}</p>}
            <p className="font-sans text-sm text-[#4A4A4A]">{item.message}</p>
          </div>
        ))}</div>
      )}
    </div>
  );
};

// ─── Settings ──────────────────────────────

const SettingsSection = () => {
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const handleUpload = async (file) => {
    setUploading(true); setSuccess(false);
    try { await adminUploadProfile(file); setSuccess(true); } catch {}
    setUploading(false);
  };
  return (
    <div className="space-y-6" data-testid="settings-manager">
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-2">Profile Document</h3>
        <p className="text-xs text-[#7A7A7A] mb-4">Upload a new PDF that visitors will download when they submit the profile request form.</p>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer"><input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])} />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5DDD3] hover:border-[#B8945A] text-sm font-sans text-[#1C2B3A]"><Upload size={14} /> {uploading ? "Uploading..." : "Choose PDF"}</div>
          </label>
          {success && <span className="text-green-600 text-sm"><Check size={14} className="inline" /> Uploaded</span>}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
        <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-2">Admin Credentials</h3>
        <p className="text-sm text-[#7A7A7A]">Email: admin@abhilash.com</p>
        <p className="text-xs text-[#7A7A7A] mt-1">Contact the developer to change admin credentials.</p>
      </div>
    </div>
  );
};

// ─── Shared Components ─────────────────────

const SectionCard = ({ title, children, onSave, saved }) => (
  <div className="bg-white rounded-lg border border-[#E5DDD3] p-5">
    <h3 className="font-serif text-base font-bold text-[#1C2B3A] mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    <div className="mt-4">
      <Button onClick={onSave} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1">
        {saved ? <><Check size={14} /> Saved</> : "Save Changes"}
      </Button>
    </div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", wide = false }) => (
  <div className={`space-y-1 ${wide ? "md:col-span-2" : ""}`}>
    <Label className="text-xs text-[#7A7A7A]">{label}</Label>
    {type === "textarea" ? (
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-[#E5DDD3] text-sm min-h-[80px]" />
    ) : (
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-[#E5DDD3] text-sm" />
    )}
  </div>
);

export default AdminDashboard;
